/**
 * AuthConfigSection
 * 
 * Seção de configuração de autenticação do endpoint.
 * Princípios: Single Responsibility, Componentização
 */

import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { notify } from '../../../../utils/toast';
import Input from '../../../../components/ui/Input';

type AuthType = 'bearer' | 'basic';

interface AuthConfigSectionProps {
    authType: AuthType;
    authToken: string;
    basicUsername: string;
    basicPassword: string;
    onAuthTypeChange: (type: AuthType) => void;
    onBasicUsernameChange: (value: string) => void;
    onBasicPasswordChange: (value: string) => void;
    onRegenerateToken: () => void;
}

const AuthConfigSection: React.FC<AuthConfigSectionProps> = ({
    authType,
    authToken,
    basicUsername,
    basicPassword,
    onAuthTypeChange,
    onBasicUsernameChange,
    onBasicPasswordChange,
    onRegenerateToken
}) => {
    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            notify.success('Token copiado!');
        } catch {
            notify.error('Erro ao copiar');
        }
    };

    return (
        <div>
            <label className="block text-sm font-medium text-default mb-2">Autenticação</label>
            <div className="flex gap-4 mb-3">
                {[
                    { value: 'bearer', label: 'Bearer Token' },
                    { value: 'basic', label: 'HTTP Basic' },
                ].map((option) => (
                    <label key={option.value} className="flex items-center cursor-pointer select-none">
                        <input
                            type="radio"
                            name="authType"
                            value={option.value}
                            checked={authType === option.value}
                            onChange={(e) => onAuthTypeChange(e.target.value as AuthType)}
                            className="h-4 w-4 text-primary focus:ring-primary border-border bg-background"
                        />
                        <span className="ml-2 text-sm text-default">{option.label}</span>
                    </label>
                ))}
            </div>

            {authType === 'bearer' && (
                <div className="bg-surface-subtle border border-border p-3 rounded-lg">
                    <label className="block text-xs font-medium text-muted mb-2">Token de Acesso</label>
                    <div className="flex items-center gap-2">
                        <Input
                            type="text"
                            readOnly
                            containerClassName="flex-1"
                            className="font-mono text-sm"
                            value={authToken}
                            onChange={() => {}}
                        />
                        <button
                            type="button"
                            onClick={() => copyToClipboard(authToken)}
                            className="p-2 text-muted hover:text-primary transition-colors"
                            title="Copiar"
                        >
                            <AppIcon name="copy" className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={onRegenerateToken}
                            className="p-2 text-muted hover:text-highlight transition-colors"
                            title="Gerar novo token"
                        >
                            <AppIcon name="refresh" className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}

            {authType === 'basic' && (
                <div className="bg-surface-subtle border border-border p-3 rounded-lg space-y-4">
                    <Input
                        label="Usuário"
                        type="text"
                        value={basicUsername}
                        onChange={(e) => onBasicUsernameChange(e.target.value)}
                        placeholder="username"
                    />
                    <Input
                        label="Senha"
                        type="password"
                        value={basicPassword}
                        onChange={(e) => onBasicPasswordChange(e.target.value)}
                        placeholder="••••••••"
                    />
                </div>
            )}
        </div>
    );
};

export default AuthConfigSection;
